import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CreateOrderModal = ({ isOpen, onClose, onSubmit, technicians, serviceTypes }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    address: '',
    serviceType: '',
    urgency: 'scheduled',
    scheduledDate: '',
    scheduledTime: '',
    assignedTechnician: '',
    description: '',
    isQuoteBased: false,
    estimatedPrice: ''
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const urgencyOptions = [
    { value: 'urgent', label: 'Urgent (dans l\'heure)' },
    { value: 'today', label: "Aujourd\'hui" },
    { value: 'scheduled', label: 'Planifié' }
  ];

  const technicianOptions = [
    { value: '', label: 'Assigner plus tard' },
    ...technicians?.map(tech => ({
      value: tech?.id,
      label: `${tech?.name} - ${tech?.specialty}`
    }))
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.customerName?.trim()) newErrors.customerName = 'Nom requis';
    if (!formData?.customerPhone?.trim()) newErrors.customerPhone = 'Téléphone requis';
    if (!formData?.address?.trim()) newErrors.address = 'Adresse requise';
    if (!formData?.serviceType) newErrors.serviceType = 'Type de service requis';
    if (!formData?.scheduledDate) newErrors.scheduledDate = 'Date requise';
    if (!formData?.scheduledTime) newErrors.scheduledTime = 'Heure requise';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg border border-border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Créer une nouvelle commande</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] scrollbar-thin">
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-foreground mb-4">Informations client</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom complet"
                  type="text"
                  required
                  value={formData?.customerName}
                  onChange={(e) => handleChange('customerName', e?.target?.value)}
                  error={errors?.customerName}
                  placeholder="Jean Dupont"
                />
                <Input
                  label="Téléphone"
                  type="tel"
                  required
                  value={formData?.customerPhone}
                  onChange={(e) => handleChange('customerPhone', e?.target?.value)}
                  error={errors?.customerPhone}
                  placeholder="06 12 34 56 78"
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData?.customerEmail}
                  onChange={(e) => handleChange('customerEmail', e?.target?.value)}
                  placeholder="jean.dupont@email.com"
                  className="md:col-span-2"
                />
                <Input
                  label="Adresse complète"
                  type="text"
                  required
                  value={formData?.address}
                  onChange={(e) => handleChange('address', e?.target?.value)}
                  error={errors?.address}
                  placeholder="15 Rue de la République, 75001 Paris"
                  className="md:col-span-2"
                />
              </div>
            </div>

            <div>
              <h3 className="font-medium text-foreground mb-4">Détails de l'intervention</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Type de service"
                  required
                  options={serviceTypes}
                  value={formData?.serviceType}
                  onChange={(value) => handleChange('serviceType', value)}
                  error={errors?.serviceType}
                  searchable
                  className="md:col-span-2"
                />
                <Select
                  label="Urgence"
                  required
                  options={urgencyOptions}
                  value={formData?.urgency}
                  onChange={(value) => handleChange('urgency', value)}
                />
                <Select
                  label="Technicien assigné"
                  options={technicianOptions}
                  value={formData?.assignedTechnician}
                  onChange={(value) => handleChange('assignedTechnician', value)}
                  searchable
                />
                <Input
                  label="Date"
                  type="date"
                  required
                  value={formData?.scheduledDate}
                  onChange={(e) => handleChange('scheduledDate', e?.target?.value)}
                  error={errors?.scheduledDate}
                />
                <Input
                  label="Heure"
                  type="time"
                  required
                  value={formData?.scheduledTime}
                  onChange={(e) => handleChange('scheduledTime', e?.target?.value)}
                  error={errors?.scheduledTime}
                />
              </div>
            </div>

            <div>
              <Input
                label="Description du problème"
                type="text"
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Décrivez le problème rencontré..."
              />
            </div>

            <div className="space-y-4">
              <Checkbox
                label="Service sur devis"
                description="Le prix sera déterminé après diagnostic"
                checked={formData?.isQuoteBased}
                onChange={(e) => handleChange('isQuoteBased', e?.target?.checked)}
              />
              {!formData?.isQuoteBased && (
                <Input
                  label="Prix estimé (€)"
                  type="number"
                  value={formData?.estimatedPrice}
                  onChange={(e) => handleChange('estimatedPrice', e?.target?.value)}
                  placeholder="150.00"
                />
              )}
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            onClick={handleSubmit}
          >
            Créer la commande
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderModal;